import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconWorldWww } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useBrandsStore } from "../Brands/brandsStore";
import { pingSiteRefresh } from "../Brands/brandsApi";

export default function SiteRefreshSettings({ brand }) {
  const { updateBrand } = useBrandsStore();
  const [url, setUrl] = useState(brand.publishWebhookUrl || "");
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [pinging, setPinging] = useState(false);

  const configured = !!brand.publishWebhookConfigured;
  const dirtyUrl = url.trim() !== (brand.publishWebhookUrl || "");
  const canSave = brand.canEdit && (dirtyUrl || secret.length > 0);

  const save = async ({ clearSecret = false } = {}) => {
    setSaving(true);
    try {
      const payload = {
        ...brand,
        publishWebhookUrl: url.trim(),
      };
      if (clearSecret) payload.publishWebhookSecret = "";
      else if (secret.length > 0) payload.publishWebhookSecret = secret;
      await updateBrand(brand.id, payload);
      setSecret("");
      notifications.show({
        title: "Saved",
        message: "The live site will refresh when you publish an article.",
        color: "green",
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Could not save the site refresh settings.",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  };

  const ping = async () => {
    setPinging(true);
    try {
      const result = await pingSiteRefresh(brand.id);
      if (result?.sent && result.statusCode >= 200 && result.statusCode < 300) {
        notifications.show({
          title: "Site answered",
          message: `HTTP ${result.statusCode}. New articles will appear after publish.`,
          color: "green",
        });
      } else if (result?.skipReason) {
        notifications.show({
          title: "Not sent",
          message: result.skipReason,
          color: "yellow",
        });
      } else {
        notifications.show({
          title: "Site did not accept the ping",
          message: result?.error || `HTTP ${result?.statusCode ?? "unknown"}`,
          color: "red",
        });
      }
    } catch {
      notifications.show({
        title: "Ping failed",
        message: "The site did not answer.",
        color: "red",
      });
    } finally {
      setPinging(false);
    }
  };

  return (
    <Stack mt="md" gap="lg">
      <div>
        <Title order={3}>Live site refresh</Title>
        <Text size="sm" c="dimmed" mt={4}>
          When you publish an article, BrandKarma sends a POST to this URL.
          The live site then rebuilds that page. Use this instead of a short
          cache window, so Vercel does not bill every crawler visit as a render.
        </Text>
      </div>

      <Paper p="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Text fw={500}>Webhook</Text>
            {configured ? (
              <Badge color="green" variant="light">Secret stored</Badge>
            ) : (
              <Badge color="gray" variant="light">No secret</Badge>
            )}
          </Group>

          <TextInput
            label="POST URL"
            description="Example: https://www.example.com/api/revalidate/. If the site uses trailing slashes, the URL must end with /."
            placeholder="https://www.example.com/api/revalidate/"
            leftSection={<IconWorldWww size={16} />}
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
            disabled={!brand.canEdit}
          />

          <PasswordInput
            label="Shared secret"
            description="Must match REVALIDATE_SECRET on the site. Leave blank to keep the stored secret."
            placeholder={configured ? "••••••••" : "Set a secret"}
            value={secret}
            onChange={(e) => setSecret(e.currentTarget.value)}
            disabled={!brand.canEdit}
          />

          <Group>
            <Button
              onClick={() => save()}
              loading={saving}
              disabled={!canSave}
            >
              Save
            </Button>
            <Button
              variant="light"
              onClick={ping}
              loading={pinging}
              disabled={!brand.canEdit || !brand.publishWebhookUrl || !configured}
            >
              Send test POST
            </Button>
            {configured && brand.canEdit && (
              <Button
                variant="subtle"
                color="red"
                loading={saving}
                onClick={() => save({ clearSecret: true })}
              >
                Clear secret
              </Button>
            )}
          </Group>
        </Stack>
      </Paper>

      <Alert color="gray" variant="light">
        <Text size="sm">
          The site must accept Authorization: Bearer and a JSON body
          {" "}
          <code>{`{ "slug": "my-post", "locale": "en" }`}</code>
          . Next.js App Router sites in this company already expose
          {" "}
          <code>/api/revalidate/</code>
          . Set the same secret in the site&apos;s Vercel environment as
          {" "}
          <code>REVALIDATE_SECRET</code>
          .
        </Text>
      </Alert>
    </Stack>
  );
}
